import { TWorkflowById, createWorkflowRequest } from '@/domains/workflows/fetchers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export interface ReportCaseParams {
  workflow: TWorkflowById;
}

/**
 * Creates a new case (workflow) for the same user when reporting/reflagging
 * Uses unique entity ID to ensure a new case is created instead of updating existing one
 */
export const useReportCaseMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ workflow }: ReportCaseParams) => {
      if (typeof window !== 'undefined') {
        window.console.log('useReportCaseMutation - mutationFn called', { 
          workflowId: workflow?.id,
          hasEntity: !!workflow?.context?.entity,
          hasWorkflowDefinition: !!workflow?.workflowDefinition?.id 
        });
      }
      
      if (!workflow?.context?.entity || !workflow?.workflowDefinition?.id) {
        if (typeof window !== 'undefined') {
          window.console.error('Invalid workflow data:', {
            hasEntity: !!workflow?.context?.entity,
            hasWorkflowDefinition: !!workflow?.workflowDefinition?.id,
            workflow: workflow
          });
        }
        throw new Error('Invalid workflow data for reporting case');
      }

      const currentEntity = workflow.context.entity;
      const entityData = (currentEntity as any).data || currentEntity || {};
      
      // Extract user email from entity data or entity ID
      // Try multiple paths: entity.data.email, entity.email, or parse from entity.id
      let userEmail = entityData.email;
      if (!userEmail && typeof currentEntity.id === 'string') {
        // Extract email from entity ID (format: email_timestamp_random)
        if (currentEntity.id.includes('@')) {
          userEmail = currentEntity.id.split('_')[0];
        } else if ((currentEntity as any).email) {
          userEmail = (currentEntity as any).email;
        }
      }
      if (!userEmail) {
        // Fallback: use entity name or ID
        userEmail = entityData.name || `${currentEntity.id}@reported.com`;
      }
      
      const userFirstName = entityData.firstName || entityData.name?.split(' ')[0] || '';
      
      // Generate unique entity ID to ensure new case creation (same format as LiveKit agent)
      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:]/g, '').slice(0, 15).replace('T', '_');
      const randomId = Math.random().toString(36).substring(2, 10);
      const uniqueEntityId = `${userEmail.replace(/[^a-zA-Z0-9@._-]/g, '_')}_${timestamp}_${randomId}`;

      // Create new context with unique entity ID but same user data
      const newContext = {
        ...workflow.context,
        entity: {
          ...currentEntity,
          id: uniqueEntityId, // Unique ID ensures new case creation
          type: (currentEntity as any).type || 'individual',
          data: {
            ...entityData,
            approvalState: 'NEW', // Reset to new/pending state
            // Preserve original email for tracking
            email: userEmail,
            firstName: userFirstName,
            lastName: entityData.lastName || '',
            // Preserve name if exists
            name: entityData.name || `${userFirstName} ${entityData.lastName || ''}`.trim() || userEmail,
            // Add report metadata
            additionalInfo: {
              ...(entityData.additionalInfo || {}),
              reportedAt: new Date().toISOString(),
              reportedFromWorkflowId: workflow.id,
              reportedBy: 'dashboard_user',
            },
          },
        },
        // Clear documents to start fresh - user will need to re-upload
        documents: [],
      };

      // Create new workflow case
      try {
        const result = await createWorkflowRequest({
          workflowDefinitionId: workflow.workflowDefinition.id,
          context: newContext,
        });
        
        if (typeof window !== 'undefined') {
          window.console.log('Create workflow result:', result);
        }
        
        // Ensure we return the result in the expected format
        if (!result) {
          throw new Error('No response from workflow creation API');
        }
        
        return result;
      } catch (error: any) {
        if (typeof window !== 'undefined') {
          window.console.error('Error in createWorkflowRequest:', error);
        }
        throw error;
      }
    },
    onSuccess: (data: any) => {
      if (typeof window !== 'undefined') {
        window.console.log('Report case success - full response:', JSON.stringify(data, null, 2));
      }
      
      // Response format from backend: { workflowRuntimeId, workflowDefinitionId, ballerineEntityId }
      // handleZodError returns the data directly, so data should be the response object
      const newWorkflowId = data?.workflowRuntimeId || data?.id || data?.workflowId;
      
      if (typeof window !== 'undefined') {
        window.console.log('Extracted workflow ID:', newWorkflowId);
      }
      
      // Invalidate queries first to refresh the case list
      void queryClient.invalidateQueries();
      
      if (newWorkflowId) {
        toast.success(`New case created successfully (ID: ${newWorkflowId}). Status: Pending`);
        setTimeout(() => {
          navigate(`/en/case-management/entities/${newWorkflowId}`);
        }, 1000);
      } else {
        // If no workflow ID found, the case might still have been created
        // Navigate to entities list to check
        if (typeof window !== 'undefined') {
          window.console.warn('No workflow ID in response, but API call succeeded. Response:', data);
        }
        toast.success('New case creation initiated. Checking case list...');
        setTimeout(() => {
          queryClient.invalidateQueries();
          navigate('/en/case-management/entities');
        }, 1000);
      }
    },
    onError: (error: any) => {
      if (typeof window !== 'undefined') {
        window.console.error('Error reporting case:', error);
      }
      const errorMessage = error?.message || error?.response?.data?.message || error?.data?.message || 'Unknown error';
      toast.error(`Failed to create new case: ${errorMessage}`);
    },
  });
};

